import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useQuote } from "../context/QuoteContext";
import { strengths } from "../data/products";
import Seo from "../components/Seo";

const ease = [0.16, 1, 0.3, 1];

export default function About() {
  const { openQuote } = useQuote();
  return (
    <div data-testid="about-page" className="pt-32">
      <Seo
        title="About Us | Clinixport Global"
        description="Clinixport Global (Clinixport LLP) is a medical equipment and hygiene supply company built around one standard: cleanliness cannot be compromised. Learn about our vision, mission and what sets us apart."
        path="/about"
        keywords="about Clinixport Global, medical supply company, hygiene supplier, healthcare supply partner"
      />
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-5">About Us</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.02] max-w-4xl">
            Cleanliness you can trust. Quality you can rely on.
          </h1>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-3">
          <Reveal><h2 className="text-2xl font-bold tracking-tight text-[#0A1128]">Who We Are</h2></Reveal>
        </div>
        <div className="lg:col-span-9">
          <Reveal>
            <p className="text-xl text-[#475569] leading-relaxed">
              Clinixport Global is a medical equipment and hygiene supply company built around one
              simple standard: <span className="text-[#0A1128] font-semibold">cleanliness cannot be compromised.</span> We
              supply a full range of surgical and non-surgical products, with a strong focus on
              protective hygiene essentials — masks, gloves, hairnets, shoe covers and aprons —
              trusted by businesses that operate in cleanliness-critical environments.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-[#475569] leading-relaxed">
              Every product we provide is selected and supplied with the same priority in mind:
              protecting people, protecting standards, and protecting reputations.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VISION / MISSION */}
      <section className="bg-white border-y border-[#0A1128]/10 py-20 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0A1128]/10 border border-[#0A1128]/10">
          {[
            { t: "Our Vision", d: "To be the most trusted global name in hygiene and protective supply — the brand industries turn to first whenever cleanliness, safety and quality cannot be compromised." },
            { t: "Our Mission", d: "To deliver consistently high-quality, dependable protective and hygiene products, backed by strict cleanliness standards and reliable global supply, so our partners can operate with confidence every single day." },
          ].map((b, i) => (
            <motion.div
              key={b.t}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.7, ease, delay: i * 0.1 }}
              className="bg-white p-10 sm:p-14 relative"
            >
              <span className="absolute top-0 left-0 w-16 h-1 bg-[#1F6FEB]" />
              <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB]">{b.t}</h3>
              <p className="mt-6 text-lg text-[#475569] leading-relaxed">{b.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHAT SETS US APART */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128]">What Sets Us Apart</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2 divide-y md:divide-y-0 divide-[#0A1128]/10">
          {strengths.map((s, i) => (
            <Reveal key={s.num} delay={(i % 2) * 0.06}>
              <div className="py-8 flex gap-6">
                <span className="text-4xl font-extrabold tracking-tighter text-[#0A1128]/15">{s.num}</span>
                <div>
                  <h3 className="text-2xl font-bold tracking-tight text-[#0A1128]">{s.title}</h3>
                  <p className="mt-3 text-[#475569] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1128] text-white py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-xl leading-tight">
            Work with a partner who treats your standards as their own.
          </h2>
          <button onClick={() => openQuote()} data-testid="about-cta-quote"
            className="shrink-0 inline-flex items-center gap-2 bg-[#1F6FEB] hover:bg-[#1556B8] transition-colors text-white px-9 py-4 font-bold text-sm tracking-widest uppercase">
            Request a Quote <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
